import dayjs from "dayjs";
import {
  TGraphData,
  TChartData,
  TDataset,
  TDatasets,
  TCustomProcessFn,
  TTableData,
  TDiseasePestData,
} from "./types";
import { TInformationCard } from "./components/InformationCardRenderer";
import SensorDataService, { SampleItem, TDataItem } from "../../services/sensorData.service";
import { t } from "../../services/locale.service";
import xlsx, { IColumn } from "json-as-xlsx";
import Images from "../../../src/assets/images";
export const generateUniqueId = (): string => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  result += Date.now().toString(36);
  return result;
};

export const fetchAndProcessSensorData = async (args: TFetchSensorDataArguments): Promise<TFetchSensorDataResponse> => {
  const {plotId, startDate, endDate, field, dataType, customProcessFn} = args;
  try {
    const graphData = await SensorDataService.getSensorData({
      plotId,
      startDate,
      endDate,
      field,
      dataType: dataType ? dataType : "daily",
    });

    const { sample, min, max, avg } = customProcessFn
      ? customProcessFn(graphData, startDate, endDate)
      : graphData;

    const sum = graphData.sum ?? 0;

    const processedData = {
      graphData: sample,
      chartData: {
        normalRange: sample,
        min,
        max,
        avg,
        sum,
      },
      data: graphData.sample as TDataItem[]
    };

    return processedData;
  } catch (error) {
    console.error("Error fetching sensor data:", error);
    throw error;
  }
};

export const fetchAndProcessDiseasePestData = async (args: TFetchDiseasePestDataArguments) => {
  const { plotId, diseaseId, startDate, endDate, dataFor,customProcessForDiseasePest } = args;
  try {
    const { res } = await SensorDataService.getDiseasePestData({
      plotId, 
      diseaseId, 
      startDate, 
      endDate,
      dataFor
    });
    // Check if customProcessForDiseasePest is provided and process the data accordingly
    const processedData = res.map((data: TDiseasePestData) => {
      if (customProcessForDiseasePest) {
        return customProcessForDiseasePest(data); 
      }
      return { ...data, color: "defaultColor" }; // Default color if no custom process provided
    });

    return processedData.map((data:LocalObj) => ({
      [diseaseId]: data.probability,
      timestamp: data.calculatedDate,
      riskLevel: data.riskLevel,
      probability: data.probability,
      color: data.color, 
    }));

  } catch(error) {
    console.error(error);
  }
};

export const processTableData = (data: Array<TDataItem>, field: string ): { data: TTableData, columns: Array<string> } => {
  const columns = getTableColumns(field);
  if(Boolean(data) && Array.isArray(data)) {
    const tableData = data.map((obj) => {
      if(field === "disease/pest") {
        return [dayjs(obj.timestamp).format("DD MMM YYYY"), Number(obj.probability).toFixed(2), obj.riskLevel];
      } else if (field === "eto") {
        const { date } = obj;
        return [obj[field], dayjs(date).format("DD/MM/YYYY, hh:mm a")];
      } else if (field === "gdd") {
        const { date } = obj;
        return [dayjs(date).format("DD/MM/YYYY, hh:mm a"), obj[field]];
      } else if(field === "lai") {
        const { datetime, cloudCoverPercentage } = obj;
        return [dayjs(datetime).format("DD/MM/YYYY, hh:mm a"), obj[field], cloudCoverPercentage];
      } else if(SensorsWithWetType.includes(field)) {
        const { timestamp } = obj;
        return [dayjs(timestamp).format("DD/MM/YYYY, hh:mm a"), obj["WetType"] ?? Number(obj[field])?.toFixed(2)];
      } else {
        const { min, max, timestamp } = obj;
        return [dayjs(timestamp).format("DD/MM/YYYY, hh:mm a"), min?.toFixed(2), max?.toFixed(2), Number(obj[field])?.toFixed(2)];
      }
    });

    const hasWetType = tableData.length > 0 && (tableData[0][1] === "Wet" || tableData[0][1] === "Dry");
    const columns = getTableColumns(field, hasWetType);

    return {
      data: tableData, 
      columns
    };
  }
  return {
    data: [],
    columns
  };
};

export const getWeatherIcon = (icon: string) => {
  icon = icon || "";
  icon = icon.toLowerCase();

  // cloudy
  if (icon.indexOf("cloud") > -1 && icon.indexOf("part") > -1) {
    return Images.weatherPartlyCloudy;
  }
  if (icon.indexOf("cloud") > -1 && icon.indexOf("most") > -1) {
    return Images.mostlyCloudy;
  }
  if (icon.indexOf("cloud") > -1) {
    return Images.weatherCloudy;
  }

  // clear or sunny
  if (icon.indexOf("clear") > -1 && icon.indexOf("part") > -1) {
    return Images.partlySunny;
  }
  if (icon.indexOf("clear") > -1 && icon.indexOf("most") > -1) {
    return Images.weatherPartlyCloudy;
  }
  if (icon.indexOf("clear") > -1) {
    return Images.weatherSunny;
  }

  // rain
  if (icon.indexOf("cold") > -1 && icon.indexOf("rain") > -1) {
    return Images.weatherSnowyRainy;
  }
  if (
    icon.indexOf("rain") > -1 ||
    icon.indexOf("shower") > -1 ||
    icon.indexOf("drizzle") > -1
  ) {
    return Images.weatherRainy;
  }
  if (icon.indexOf("hail") > -1) {
    return Images.hail;
  }
  if (icon.indexOf("thunder") > -1) {
    return Images.weatherLightning;
  }

  // ice
  if (icon.indexOf("ice") > -1) {
    return Images.ice;
  }
  if (icon.indexOf("flurries") > -1 || icon.indexOf("snow") > -1) {
    return Images.weatherSnowy;
  }

  // fog
  if (icon.indexOf("fog") > -1) {
    return Images.fogLight;
  }

  //  hazy sunshine
  if (icon.indexOf("smoke") > -1 || icon.indexOf("haz") > -1) {
    return Images.hazySunshine;
  }

  // wind
  if (icon.indexOf("wind") > -1) {
    return Images.wind;
  }
  if (icon.indexOf("hurricane") > -1) {
    return Images.hurricane;
  }
};


export const generateChartDatasets = (graphData: TGraphData, field: string, timeframe = "daily") => {
  // console.log(graphData);
  const dataset: TDataset = {
    label:"",
    data: [],
    borderColor: "green",
    backgroundColor: "rgba(59, 254, 0, 0.2)",
    fill: "start",
    pointBackgroundColor: [],  
    pointBorderColor: [],  
    borderWidth: 1,
    pointRadius: 3,
  };
  const defaultColor = "green"; // Default color if undefined

  const timestampFormat = timeframe !== "day" ? "DD MMM" : "DD MMM, hh a";

  const timestamps = (graphData ?? []).map((data) => {
    if("timestamp" in data) {
      return dayjs(data.timestamp).format(timestampFormat);
    }
    return "";
  }
  );

  (graphData ?? []).forEach((data) => {
    dataset.data.push((data as SampleItem)[field] as number);
  });

  (graphData ?? []).forEach((data) => {
    const sample = data as SampleItem;
    dataset.data.push(sample[field] as number);
    const color = sample.color as string || defaultColor;  
    (dataset.pointBackgroundColor as string[]).push(color);
    (dataset.pointBorderColor as string[]).push(color);
  });

  const datasets: TDatasets = [dataset];

  return { datasets, timestamps };
};

/**
 * Charts Inforamation Cards
 */

export const generateInformationCardData = (
  chartData: TChartData,
  field: string,
  unit: string
): Array<TInformationCard> => {
  const hasChartDataMin = chartData?.min?.[field] != null;
  const hasChartDataMax = chartData?.max?.[field] != null;
  const hasChartDataAvg = chartData?.avg != null;

  const informationCards: Array<TInformationCard> = [];

  if (hasChartDataMin) {
    const minValue = chartData.min.min ?? chartData.min[field];
    if (minValue !== undefined) {
      informationCards.push({
        cardTitle: t("lowest"),
        value: Number((minValue as number).toFixed(1)),
        unit,
        information: dayjs(chartData.min.timestamp).format("DD/MM/YYYY, hh:mm a"),
      });
    }
  }

  if (hasChartDataAvg) {
    informationCards.push({
      cardTitle: t("average"),
      value: Number(chartData.avg.toFixed(1)),
      unit,
      information: "*" + t("onAverage"),
    });
  }

  if (hasChartDataMax) {
    const maxValue = chartData.max.max ?? chartData.max[field];
    if (maxValue !== undefined) {
      informationCards.push({
        cardTitle: t("highest"),
        value: Number((maxValue as number).toFixed(1)),
        unit,
        information: dayjs(chartData.max.timestamp).format("DD/MM/YYYY, hh:mm a"),
      });
    }
  }

  return informationCards;
};

const getTableColumns = (field: string, hasWetType = false) => {
  const MinMaxHeader = [t("Date"), t("Minimum"), t("Maximum"), t("average")];
  const WetTypeHeader = [t("Date"), "WetType"];
  const SensorNameHeader = [t("Date"), t(field)];
  const DiseasePestHeader = [t("Date"), t("probability"), t("RiskLevel")];

  if(field === "disease/pest") {
    return DiseasePestHeader;
  }

  if(SensorsWithWetType.includes(field)) {
    return hasWetType ? WetTypeHeader : SensorNameHeader;
  }
  return MinMaxHeader;
};

export const getDataType = (field: string, startDate: string, endDate: string): "daily" | "hourly" => {
  const diff = dayjs(endDate).diff(startDate, "days");

  if(field === "leafWetness") {
    return diff > 1 ? "daily" : "hourly";
  }

  const dataType = diff < 31 ? "hourly" : "daily";

  return dataType;
};

export const generateDownloadData = (data: Array<TDataItem>) => {
  return data?.map((obj) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {x, y, ...rest} = obj;

    return rest;
  });
};

export const downloadXLSX = (data: TXlsxData, fileName = "sensor-data") => {
  if(!data || data.length === 0) {
    alert("No Data!");
    return;
  }
  const columns = generateXlsxColumns(data);
  
  const sheetData = [{
    sheet: "Data",
    columns,
    content: data
  }];

  const settings = {
    fileName,
    extraLength: 3, // A bigger number means that columns will be wider
    writeOptions: {} // Style options from https://github.com/SheetJS/sheetjs#writing-options
  };

  xlsx(sheetData, settings);
};

const generateXlsxColumns = (data: TXlsxData) => {
  return Object.keys(data[0]).map(key => ({label: key, value: key})) as IColumn[];
};


/**
 * ================== TYPES ======================
 */

type TXlsxData = Array<Record<string, string | number | Date | boolean>>;

export type TFetchSensorDataArguments = {
  plotId: string,
  startDate: string,
  endDate: string,
  field: string,
  dataType: "daily" | "hourly",
  customProcessFn?: TCustomProcessFn,
}

export type TFetchSensorDataResponse = { graphData: TGraphData; chartData: TChartData, data?: Array<TDataItem> };

export type TFetchDiseasePestDataArguments = {
  plotId: string;
  startDate: string;
  endDate: string;
  diseaseId: string;
  dataFor: "Disease" | "Pest";
  customProcessForDiseasePest?: (data: TDiseasePestData) => TDiseasePestData & { 
    color: string; 
  };
}

export const generateColorForItem = (
  value: number,
  minThresholdValue: number,
  minOptimalValue: number,
  maxOptimalValue: number,
  maxThresholdValue: number
): string => {
  if (value < minThresholdValue) return "red";
  if (value >= minThresholdValue && value < minOptimalValue) return "#F4D03F";
  if (value >= minOptimalValue && value <= maxOptimalValue) return "#0c9611";
  if (value > maxOptimalValue && value <= maxThresholdValue) return "#F4D03F";
  if (value > maxThresholdValue) return "red";
  return "#0c9611";
};

/**
 * ================== CONSTANTS ======================
 */

const SensorsWithWetType = ["deltaT", "VPD", "leafWetness", "rainFall"];


/**
 * ================== LOCAL INTERFECE ======================
 */
interface LocalObj {
  calculatedDate:string,
  color:string,
  probability:number,
  riskLevel:string
}